import useSidebarStack from '@/common/sidebar/useSidebarStack.ts';
import { ActionIcon, Group, Stack, Tabs, Title } from '@mantine/core';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import SalesOrderNotesTab from '@/features/finance/sales-orders/components/detail-tabs/SalesOrderNotesTab.tsx';
import SalesOrderWorkOrdersTab from '@/features/finance/sales-orders/components/detail-tabs/SalesOrderWorkOrdersTab.tsx';
import SalesOrderWorkOrdersTabBody from '@/features/finance/sales-orders/components/detail-tabs/SalesOrderWorkOrdersTabBody.tsx';
import SalesOrderNotesTabBody from '@/features/finance/sales-orders/components/detail-tabs/SalesOrderNotesTabBody.tsx';
import type { ExtraSidebarParams } from '@/common/sidebar/sidebar-registry.tsx';
import CollapsedSidebar from '@/common/sidebar/CollapsedSidebar.tsx';
import { useSalesOrderControllerGetSalesOrderSuspense } from '@/generated/api/sales-order.ts';

export default function SalesOrderDetailsSidebar({
  salesOrderId,
  defaultTab,
  collapsed,
  depth,
  hovered,
}: {
  salesOrderId: string;
  defaultTab?: string;
} & ExtraSidebarParams) {
  const sidebarStack = useSidebarStack();

  const salesOrder = useSalesOrderControllerGetSalesOrderSuspense(salesOrderId);

  if (collapsed) {
    return (
      <CollapsedSidebar
        title={`Sales Order #${salesOrder.data.externalId!}`}
        depth={depth}
        hovered={hovered}
      />
    );
  }

  return (
    <Stack
      gap="md"
      h="100%"
      style={{
        overflow: 'hidden',
      }}
    >
      <Group>
        <ActionIcon
          size="sm"
          variant="transparent"
          onClick={() => sidebarStack.popItem()}
        >
          <ArrowLeftIcon size={32} />
        </ActionIcon>

        <Title order={4}>Sales Order</Title>
      </Group>

      <Tabs
        defaultValue={defaultTab ?? 'notes'}
        styles={{
          tab: {
            minHeight: 32,
            paddingBlock: 6,
            paddingInline: 8,
            fontSize: 13,
          },
          tabSection: {
            '&[data-position="left"]:not(:only-child)': {
              marginInlineEnd: 5,
            },
            '&[data-position="right"]:not(:only-child)': {
              marginInlineStart: 5,
            },
          },
        }}
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          flexWrap: 'nowrap',
        }}
      >
        <Tabs.List
          mb="sm"
          style={{
            flexShrink: 0,
            flexWrap: 'nowrap',
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          <SalesOrderNotesTab salesOrderId={salesOrderId} />
          <SalesOrderWorkOrdersTab salesOrderId={salesOrderId} />
        </Tabs.List>
        <SalesOrderNotesTabBody salesOrderId={salesOrderId} />
        <SalesOrderWorkOrdersTabBody salesOrderId={salesOrderId} />
      </Tabs>
    </Stack>
  );
}
